import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Button, Paper, Snackbar, Alert, Typography, List, ListItem, ListItemText, useMediaQuery, useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import QuestionnaireSection from './QuestionnaireSection';
import QuestionnaireVersionBadge from './QuestionnaireVersionBadge';
import { getQuestionnaireTemplate, getLatestQuestionnaireTemplate, CURRENT_QUESTIONNAIRE_VERSION } from './questionnaireTemplate';
import AiAssistPanel from '../../AiAssistPanel';
import { useAuth } from '../../../hooks/useAuth';
import '../../../styles/FHIRQuestionnaire.css';

const AI_ASSIST_SECTIONS = new Set(['assessmentPlan']);

/** Maps AI response keys → questionnaire response field linkIds */
const AI_FIELD_MAPPING = {
  refined_symptoms: 'ccDescription',
  refined_hpi: 'hpiOnset',
  refined_pmh: 'pmhOtherIllnesses',
  refined_medications: 'medName',
  refined_allergies: 'allergySubstance',
  refined_family_history: 'fhCondition',
  refined_social_history: 'shSocialHistory',
  refined_exam_general: 'examGeneral',
  refined_exam_heent: 'examHEENT',
  refined_exam_cardiovascular: 'examCardiovascular',
  refined_exam_respiratory: 'examRespiratory',
  refined_exam_abdomen: 'examAbdomen',
  refined_exam_extremities: 'examExtremities',
  refined_exam_skin: 'examSkin',
  refined_exam_neuro: 'examNeuro',
  suggested_diagnosis: 'diagnosis',
};

const AI_PANEL_SECTIONS = [
  { key: 'refined_symptoms', title: 'Chief Complaint', icon: '🔍' },
  { key: 'refined_hpi', title: 'History of Present Illness', icon: '📝' },
  { key: 'refined_pmh', title: 'Past Medical History', icon: '📋' },
  { key: 'refined_medications', title: 'Current Medications', icon: '💊' },
  { key: 'refined_allergies', title: 'Allergies / Adverse Reactions', icon: '⚠️' },
  { key: 'refined_family_history', title: 'Family History', icon: '👪' },
  { key: 'refined_social_history', title: 'Social History', icon: '🏠' },
  { key: 'refined_exam_general', title: 'Exam — General', icon: '🩺' },
  { key: 'refined_exam_heent', title: 'Exam — ENT', icon: '🩺' },
  { key: 'refined_exam_cardiovascular', title: 'Exam — Cardiovascular', icon: '🩺' },
  { key: 'refined_exam_respiratory', title: 'Exam — Respiratory', icon: '🩺' },
  { key: 'refined_exam_abdomen', title: 'Exam — Abdomen', icon: '🩺' },
  { key: 'refined_exam_extremities', title: 'Exam — Extremities', icon: '🩺' },
  { key: 'refined_exam_skin', title: 'Exam — Skin', icon: '🩺' },
  { key: 'refined_exam_neuro', title: 'Exam — Neurological', icon: '🩺' },
  { key: 'suggested_diagnosis', title: 'Diagnosis', icon: '💊' },
];

const FHIRQuestionnaire = ({ admissionId, onClose }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { hasPermission, token } = useAuth();
  const [activeSection, setActiveSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [status, setStatus] = useState('in-progress');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [templateVersion, setTemplateVersion] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(getLatestQuestionnaireTemplate());
  const [hasSaved, setHasSaved] = useState(false);

  // AI Assist state
  const [aiPanelVisible, setAiPanelVisible] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiCached, setAiCached] = useState(false);

  const sections = currentTemplate.item || [];
  const activeSectionLinkId = sections[activeSection]?.linkId;
  const showAiAssist = AI_ASSIST_SECTIONS.has(activeSectionLinkId)
    && hasPermission('/ai-assist/admission-questionnaire-suggestions', 'Execute');

  useEffect(() => {
    loadQuestionnaire();
  }, [admissionId]);

  const loadQuestionnaire = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/questionnaire/${admissionId}`);
      if (data) {
        setResponses(data.questionnaire_response || {});
        setStatus(data.status);
        setHasSaved(true);

        const storedVersion = data.template_version;
        setTemplateVersion(storedVersion);

        if (storedVersion) {
          const versionedTemplate = getQuestionnaireTemplate(storedVersion);
          setCurrentTemplate(versionedTemplate);
        } else {
          const latestTemplate = getLatestQuestionnaireTemplate();
          setCurrentTemplate(latestTemplate);
          setTemplateVersion(CURRENT_QUESTIONNAIRE_VERSION);
        }
      } else {
        const latestTemplate = getLatestQuestionnaireTemplate();
        setCurrentTemplate(latestTemplate);
        setTemplateVersion(CURRENT_QUESTIONNAIRE_VERSION);
        setHasSaved(false);
      }
    } catch (error) {
      console.error('Error loading questionnaire:', error);
      const latestTemplate = getLatestQuestionnaireTemplate();
      setCurrentTemplate(latestTemplate);
      setTemplateVersion(CURRENT_QUESTIONNAIRE_VERSION);
      setHasSaved(false);
    }
  };

  const getSectionStatus = (section) => {
    if (section.repeats) {
      const sectionData = responses[section.linkId];
      return Array.isArray(sectionData) && sectionData.some(item => 
        Object.values(item).some(value => value && value !== '')
      );
    } else {
      return section.item?.some(item => {
        const value = responses[item.linkId];
        return value && value !== '' && (!Array.isArray(value) || value.length > 0);
      });
    }
  };

  const isEmptyValue = (value) => {
    if (value === undefined || value === null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    return false;
  };

  const validateMandatoryFields = () => {
    const errors = {};
    const nextFieldErrors = {};

    const addSectionError = (sectionLinkId, label) => {
      if (!sectionLinkId) return;
      const existing = errors[sectionLinkId];
      if (!existing) {
        errors[sectionLinkId] = `Missing required fields: ${label}`;
      } else if (!existing.includes(label)) {
        errors[sectionLinkId] = `${existing}, ${label}`;
      }
    };

    const validateItems = (items, sectionLinkId, responseData = responses) => {
      items?.forEach((item) => {
        if (item.type === 'group' && item.item) {
          const nextSection = item.linkId || sectionLinkId;
          if (item.repeats) {
            const sectionData = responseData[item.linkId];
            if (Array.isArray(sectionData)) {
              sectionData.forEach((groupItem) => validateItems(item.item, nextSection, groupItem));
            }
          } else {
            validateItems(item.item, nextSection, responseData);
          }
          return;
        }

        if (item.required && isEmptyValue(responseData[item.linkId])) {
          nextFieldErrors[item.linkId] = `${item.text} is required`;
          addSectionError(sectionLinkId, item.text);
        }
      });
    };

    validateItems(currentTemplate.item);

    // Keep existing required clinical fields even if an older template omits required flags
    if (isEmptyValue(responses.ccDescription) && !nextFieldErrors.ccDescription) {
      nextFieldErrors.ccDescription = 'Description is required';
      addSectionError('chiefComplaint', 'Description');
    }
    if (isEmptyValue(responses.diagnosis) && !nextFieldErrors.diagnosis) {
      nextFieldErrors.diagnosis = 'Diagnosis is required';
      addSectionError('assessmentPlan', 'Diagnosis');
    }

    return { errors, fieldErrors: nextFieldErrors };
  };

  const handleSave = async (isComplete = false) => {
    const { errors: allErrors, fieldErrors: allFieldErrors } = validateMandatoryFields();
    const adminFieldIds = ['patientName', 'visitDate', 'attendingPhysician'];
    const errors = isComplete
      ? allErrors
      : (allErrors.administrative ? { administrative: allErrors.administrative } : {});
    const nextFieldErrors = isComplete
      ? allFieldErrors
      : Object.fromEntries(Object.entries(allFieldErrors).filter(([id]) => adminFieldIds.includes(id)));

    setValidationErrors(errors);
    setFieldErrors(nextFieldErrors);

    if (Object.keys(errors).length > 0) {
      const firstErrorIndex = sections.findIndex((section) => errors[section.linkId]);
      if (firstErrorIndex >= 0) {
        setActiveSection(firstErrorIndex);
      }
      setNotification({
        open: true,
        message: isComplete
          ? 'Please complete all required fields before marking as complete'
          : 'Please fill in Patient Name, Date of Admission, and Attending Physician',
        severity: 'error'
      });
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/questionnaire/${admissionId}`, {
        response: responses,
        status: isComplete ? 'completed' : 'in-progress',
        template_version: templateVersion || CURRENT_QUESTIONNAIRE_VERSION
      });
      setHasSaved(true);
      if (isComplete) {
        setStatus('completed');
        onClose && onClose();
      }
      setNotification({
        open: true,
        message: isComplete ? 'Questionnaire completed successfully!' : 'Progress saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      setNotification({
        open: true,
        message: 'Error saving questionnaire',
        severity: 'error'
      });
    }
  };

  const handleResponseChange = (linkId, value) => {
    setResponses(prev => ({
      ...prev,
      [linkId]: value
    }));
    if (fieldErrors[linkId]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[linkId];
        return next;
      });
    }
    if (validationErrors.administrative && ['patientName', 'visitDate', 'attendingPhysician'].includes(linkId)) {
      setValidationErrors(prev => ({ ...prev, administrative: undefined }));
    }
    if (validationErrors.chiefComplaint && ['ccDescription', 'ccDuration', 'ccSeverity'].includes(linkId)) {
      setValidationErrors(prev => ({ ...prev, chiefComplaint: undefined }));
    }
    if (validationErrors.assessmentPlan && ['assessment', 'diagnosis', 'plan'].includes(linkId)) {
      setValidationErrors(prev => ({ ...prev, assessmentPlan: undefined }));
    }
  };

  const handleAiAssist = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    setAiCached(false);
    setAiPanelVisible(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/ai/admission-questionnaire-suggestions`,
        { admission_id: admissionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAiSuggestions(response.data.data);
        setAiCached(response.data.cached || false);
      } else {
        setAiError(response.data.error || 'Unexpected error');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to get AI suggestions';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiApplyAll = (fieldUpdates) => {
    setResponses(prev => ({ ...prev, ...fieldUpdates }));
    setAiPanelVisible(false);
    setNotification({
      open: true,
      message: 'AI suggestions applied. Review and save when ready.',
      severity: 'success',
    });
  };

  const getSectionBorderColor = (section) => {
    if (validationErrors[section.linkId]) return '#f44336';
    if (getSectionStatus(section)) return '#4caf50';
    return '#e0e0e0';
  };

  return (
    <Box sx={{ display: 'flex', height: '80vh', bgcolor: '#fafafa' }}>
      {/* Left Sidebar Navigation */}
      <Paper 
        sx={{ 
          width: 280, 
          p: 2, 
          bgcolor: 'white',
          borderRadius: '8px 0 0 8px',
          overflow: 'auto'
        }}
        elevation={0}
      >
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '1.1rem' }}>
          Questionnaire Sections
        </Typography>
        
        {/* Version Badge */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <QuestionnaireVersionBadge version={templateVersion} />
        </Box>
        
        <List dense sx={{ p: 0 }}>
          {sections.map((section, index) => {
            const hasData = getSectionStatus(section);
            const hasError = validationErrors[section.linkId];
            
            return (
              <React.Fragment key={section.linkId}>
                <ListItem
                  button
                  selected={activeSection === index}
                  onClick={() => setActiveSection(index)}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    border: `2px solid ${getSectionBorderColor(section)}`,
                    bgcolor: hasData ? 'success.50' : 'grey.50',
                    '&.Mui-selected': {
                      bgcolor: hasData ? 'success.100' : 'grey.100',
                      '&:hover': {
                        bgcolor: hasData ? 'success.100' : 'grey.100',
                      }
                    },
                    '&:hover': {
                      bgcolor: hasData ? 'success.75' : 'grey.75',
                    },
                    py: 1
                  }}
                >
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    {hasData ? (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: '1.2rem' }} />
                    )}
                  </Box>
                  <ListItemText 
                    primary={section.text}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: activeSection === index ? 600 : 400,
                      color: hasError ? 'error.main' : 'text.primary'
                    }}
                  />
                </ListItem>
                {hasError && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'error.main', 
                      fontSize: '0.7rem',
                      pl: 4,
                      display: 'block',
                      mb: 1
                    }}
                  >
                    {hasError}
                  </Typography>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Paper 
          sx={{ 
            flex: 1, 
            p: 2, 
            m: 0,
            borderRadius: '0 8px 8px 0',
            overflow: 'auto',
            bgcolor: 'white'
          }}
          elevation={0}
        >
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 1,
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'primary.main',
                fontSize: '1.2rem',
                fontWeight: 600,
              }}
            >
              {sections[activeSection]?.text}
            </Typography>
            {showAiAssist && (
              <button
                type="button"
                className="ai-assist-trigger"
                onClick={handleAiAssist}
                disabled={aiLoading || !hasSaved}
                title={!hasSaved ? 'Save Progress first' : 'Get AI suggestions for chief complaint and diagnosis'}
              >
                <span className="ai-sparkle">✨</span>
                Refine Notes
              </button>
            )}
          </Box>
          
          <Box sx={{ pb: 2 }}>
            <QuestionnaireSection
              section={sections[activeSection]}
              responses={responses}
              onChange={handleResponseChange}
              compact={isSmallScreen}
              fieldErrors={fieldErrors}
            />
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'white',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Section {activeSection + 1} of {sections.length}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              size="small"
              onClick={() => handleSave(false)}
              variant="outlined"
            >
              Save Progress
            </Button>
            <Button 
              size="small"
              variant="contained" 
              color="primary"
              onClick={() => handleSave(true)}
            >
              Complete Questionnaire
            </Button>
          </Box>
        </Box>
      </Box>

      <AiAssistPanel
        visible={aiPanelVisible}
        onClose={() => setAiPanelVisible(false)}
        onApplyAll={handleAiApplyAll}
        suggestions={aiSuggestions}
        loading={aiLoading}
        error={aiError}
        cached={aiCached}
        fieldMapping={AI_FIELD_MAPPING}
        onRetry={handleAiAssist}
        mode="suggestions"
        sections={AI_PANEL_SECTIONS}
        panelTitle="Admission Suggestions"
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FHIRQuestionnaire;
